export const emitEvent = (event, data) => {
    document.dispatchEvent(new CustomEvent(event, {
        detail: data
    }))
}

export const onEvent = (event, callback) => {
    document.addEventListener(event, callback)
}

export const onLoaded = (callback) => {
    onEvent('DOMContentLoaded', callback)
}

// Initialize the initial state for the current page
export const initializeInitialState = () => {
    if (window.history && window.history.replaceState) {
        // Replace the initial state with our own state object
        // This ensures that when users navigate back to the initial page,
        // we have a proper state object to work with
        window.history.replaceState(
            {
                url: window.location.href,
                source: 'ajaxpress'
            },
            '',
            window.location.href
        );
    }
}

export const getContainer = () => {
    // Premium feature: custom target requires valid license
    const license = ajaxpress_vars?.license || {}
    const isLicenseValid = !!(license.key && license.status === 'active')
    
    let target = 'body'
    if (isLicenseValid && ajaxpress_vars.settings.target) {
        target = ajaxpress_vars.settings.target
    }
    
    return document.querySelector(target) || document.body
}

// Operations
window.ajaxpress_state = {}

// Global tracker for loaded external scripts (prevents re-execution across navigations)
window.ajaxpress_loaded_scripts = window.ajaxpress_loaded_scripts || new Set()

/**
 * Check if script is AjaxPress's own frontend script
 */
const isAjaxPressScript = (src) => {
    if (!src) return false
    const normalizedSrc = normalizeUrl(src)
    return normalizedSrc.includes('ajaxpress.min.js') || (normalizedSrc.includes('/ajaxpress/') && normalizedSrc.includes('.min.js'))
}

/**
 * Initialize the script tracker with scripts already on the page
 * This prevents re-execution of framework bundles and other external scripts
 */
export const initializeScriptTracker = () => {
    const existingScripts = document.querySelectorAll('script[src]')
    existingScripts.forEach(script => {
        if (script.src && !isAjaxPressScript(script.src)) {
            const normalizedSrc = normalizeUrl(script.src)
            window.ajaxpress_loaded_scripts.add(normalizedSrc)
        }
    })
}

export const fetchContent = async (url = window.location.href, source = 'click') => {
    return new Promise ( (resolve, reject) => {
        emitEvent('ajaxpress-loading', {
            url, source
        })
        const headers = new Headers();
        headers.set('AjaxPress-Ajax', 'true');
        // Fetch content from server.
        fetch(url, {
            headers: headers
        })
        .then( response => response.text() )
        .then( html => {
            window.ajaxpress_state.html = html
            emitEvent('ajaxpress-fetched', {
                url, html: html || '', source
            })
            resolve ( html )
        }).catch( err => {
            reject(err)
        })
    })
}


/**
 * Normalize URL to absolute URL
 */
const normalizeUrl = (url) => {
    if (!url) return url
    // If already absolute, return as is
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
        return url
    }
    // If relative, make it absolute
    if (url.startsWith('/')) {
        return window.location.origin + url
    }
    // Relative to current page
    const baseUrl = window.location.href.split('?')[0]
    const basePath = baseUrl.substring(0, baseUrl.lastIndexOf('/') + 1)
    return basePath + url
}

/**
 * Check if script/stylesheet already exists (normalized URL comparison)
 */
const assetExists = (url, type = 'script') => {
    const normalizedUrl = normalizeUrl(url)
    const selector = type === 'script' ? 'script[src]' : 'link[rel="stylesheet"][href]'
    const elements = document.querySelectorAll(selector)
    
    for (const el of elements) {
        const existingUrl = type === 'script' ? el.src : el.href
        const normalizedExisting = normalizeUrl(existingUrl)
        if (normalizedUrl === normalizedExisting || existingUrl === url) {
            return true
        }
    }
    return false
}

/**
 * Load a script dynamically
 */
const loadScript = (src, async = true) => {
    return new Promise((resolve, reject) => {
        const normalizedSrc = normalizeUrl(src)
        
        // Check if script already exists
        if (assetExists(normalizedSrc, 'script')) {
            resolve()
            return
        }

        const script = document.createElement('script')
        script.src = normalizedSrc
        script.async = async
        script.onload = () => {
            resolve()
        }
        script.onerror = () => {
            console.warn('AjaxPress: Failed to load script:', normalizedSrc)
            reject(new Error(`Failed to load script: ${normalizedSrc}`))
        }
        document.head.appendChild(script)
    })
}

/**
 * Load a stylesheet dynamically
 */
const loadStylesheet = (href) => {
    return new Promise((resolve, reject) => {
        const normalizedHref = normalizeUrl(href)
        
        // Check if stylesheet already exists
        if (assetExists(normalizedHref, 'stylesheet')) {
            resolve()
            return
        }

        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = normalizedHref
        link.onload = () => {
            resolve()
        }
        link.onerror = () => {
            console.warn('AjaxPress: Failed to load stylesheet:', normalizedHref)
            reject(new Error(`Failed to load stylesheet: ${normalizedHref}`))
        }
        document.head.appendChild(link)
    })
}

/**
 * Execute inline scripts from a container
 */
const executeScripts = (container) => {
    const scripts = container.querySelectorAll('script')
    const executedInThisCycle = new Set()
    
    scripts.forEach((oldScript, index) => {
        // Skip AjaxPress's own script
        if (oldScript.src && isAjaxPressScript(oldScript.src)) {
            return
        }
        
        // Create a unique identifier for this script to prevent duplicate execution in same cycle
        const scriptId = oldScript.src || (oldScript.textContent ? oldScript.textContent.substring(0, 100) : 'empty-' + index)
        
        if (executedInThisCycle.has(scriptId)) {
            return
        }
        
        executedInThisCycle.add(scriptId)
        
        if (oldScript.src) {
            // External script - check if already loaded to prevent re-execution
            const normalizedSrc = normalizeUrl(oldScript.src)

            // Allow force reload with data-ajaxpress-reload attribute
            const forceReload = oldScript.hasAttribute('data-ajaxpress-reload')

            // Skip if already loaded (unless force reload)
            if (!forceReload && window.ajaxpress_loaded_scripts.has(normalizedSrc)) {
                return
            }

            const newScript = document.createElement('script')
            Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value)
            })
            newScript.src = normalizedSrc

            // Mark as executed
            newScript.setAttribute('data-ajaxpress-executed', 'true')

            // Track this script as loaded
            window.ajaxpress_loaded_scripts.add(normalizedSrc)

            // Remove old script and add new one to trigger execution
            oldScript.remove()
            container.appendChild(newScript)
        } else {
            // Inline script - execute it by replacing
            if (oldScript.textContent && oldScript.textContent.trim()) {
                // Create new script element
                const newScript = document.createElement('script')
                Array.from(oldScript.attributes).forEach(attr => {
                    newScript.setAttribute(attr.name, attr.value)
                })
                newScript.textContent = oldScript.textContent

                // Mark as executed
                newScript.setAttribute('data-ajaxpress-executed', 'true')

                // Replace old script with new one to trigger execution
                const parent = oldScript.parentNode
                if (parent) {
                    parent.replaceChild(newScript, oldScript)
                } else {
                    // If no parent, append to container
                    container.appendChild(newScript)
                }
            }
        }
    })
}

/**
 * Update head elements (scripts, stylesheets, meta tags, inline styles)
 */
const updateHead = (newDocument) => {
    const currentHead = document.head
    const newHead = newDocument.head
    
    // Process new head elements
    const scriptsToLoad = []
    const stylesheetsToLoad = []
    const inlineStyles = []
    
    Array.from(newHead.children).forEach(newElement => {
        const tagName = newElement.tagName.toLowerCase()
        
        if (tagName === 'script') {
            // Skip if already executed
            if (newElement.hasAttribute('data-ajaxpress-executed')) {
                return
            }
            
            const src = newElement.getAttribute('src')
            // Skip AjaxPress's own script
            if (src && isAjaxPressScript(src)) {
                return
            }
            
            if (src) {
                // External script
                const normalizedSrc = normalizeUrl(src)
                if (!assetExists(normalizedSrc, 'script')) {
                    scriptsToLoad.push({
                        src: normalizedSrc,
                        async: newElement.getAttribute('async') !== null,
                        defer: newElement.getAttribute('defer') !== null,
                        type: newElement.getAttribute('type') || 'text/javascript'
                    })
                }
            } else {
                // Inline script - execute it
                const scriptContent = newElement.textContent
                if (scriptContent && scriptContent.trim()) {
                    try {
                        // Create and execute inline script
                        const script = document.createElement('script')
                        script.textContent = scriptContent
                        Array.from(newElement.attributes).forEach(attr => {
                            script.setAttribute(attr.name, attr.value)
                        })
                        script.setAttribute('data-ajaxpress-executed', 'true')
                        document.head.appendChild(script)
                    } catch (err) {
                        console.warn('AjaxPress: Failed to execute inline script:', err)
                    }
                }
            }
        } else if (tagName === 'link') {
            const rel = newElement.getAttribute('rel')
            const href = newElement.getAttribute('href')
            
            if (rel === 'stylesheet' && href) {
                const normalizedHref = normalizeUrl(href)
                if (!assetExists(normalizedHref, 'stylesheet')) {
                    stylesheetsToLoad.push(normalizedHref)
                }
            } else if (rel === 'preload' || rel === 'prefetch') {
                // Handle preload/prefetch links
                const normalizedHref = normalizeUrl(href)
                const existingPreload = currentHead.querySelector(`link[rel="${rel}"][href="${normalizedHref}"]`)
                if (!existingPreload && href) {
                    const link = document.createElement('link')
                    link.rel = rel
                    link.href = normalizedHref
                    if (newElement.getAttribute('as')) {
                        link.setAttribute('as', newElement.getAttribute('as'))
                    }
                    currentHead.appendChild(link)
                }
            }
        } else if (tagName === 'style') {
            // Inline styles - append to head
            const styleContent = newElement.textContent
            if (styleContent && styleContent.trim()) {
                const style = document.createElement('style')
                style.textContent = styleContent
                if (newElement.getAttribute('id')) {
                    style.id = newElement.getAttribute('id')
                }
                if (newElement.getAttribute('media')) {
                    style.setAttribute('media', newElement.getAttribute('media'))
                }
                currentHead.appendChild(style)
            }
        } else if (tagName === 'meta') {
            // Update meta tags
            const name = newElement.getAttribute('name') || newElement.getAttribute('property')
            const content = newElement.getAttribute('content')
            
            if (name && content) {
                let existingMeta = currentHead.querySelector(`meta[name="${name}"], meta[property="${name}"]`)
                if (!existingMeta) {
                    existingMeta = document.createElement('meta')
                    if (newElement.getAttribute('name')) {
                        existingMeta.setAttribute('name', name)
                    } else {
                        existingMeta.setAttribute('property', name)
                    }
                    currentHead.appendChild(existingMeta)
                }
                existingMeta.setAttribute('content', content)
            }
        }
    })
    
    // Load new scripts and stylesheets
    return Promise.all([
        ...scriptsToLoad.map(scriptInfo => {
            return loadScript(scriptInfo.src, scriptInfo.async).catch(err => {
                console.warn('AjaxPress: Failed to load script:', scriptInfo.src, err)
            })
        }),
        ...stylesheetsToLoad.map(href => {
            return loadStylesheet(href).catch(err => {
                console.warn('AjaxPress: Failed to load stylesheet:', href, err)
            })
        })
    ])
}

export const updateContent = async (html, source = 'navigation') => {
    /**
     * Updates the main content container with new HTML and updates the document title.
     * Handles scripts, stylesheets, and reinitializes JavaScript.
     * @param {string} html - The HTML content to update
     * @param {string} source - The source of the update ('navigation', 'comment', etc.)
     */
    var currentContainer = getContainer();
    if ( !currentContainer ) return

    // Store source for event handlers
    window.ajaxpress_state.updateSource = source

    // Update the URL.
    // Only push state if the new URL is different from the current location to avoid pushing old data.
    if ( window.history && window.history.pushState && window.ajaxpress_state.url !== window.location.href ) {
        window.history.pushState(
            {
                url: window.ajaxpress_state.url,
                source: 'ajaxpress'
            },
            '',
            window.ajaxpress_state.url
        );
    }

    // Parse the new HTML
    var parser = new window.DOMParser();
    var newDocument = parser.parseFromString(html, 'text/html');

    // Update title.
    document.title = newDocument.title;

    // Update head elements (scripts, stylesheets, meta tags)
    await updateHead(newDocument);

    // Get the new container content (Premium feature: custom target requires valid license)
    const license = ajaxpress_vars?.license || {}
    const isLicenseValid = !!(license.key && license.status === 'active')
    
    let target = 'body'
    if (isLicenseValid && ajaxpress_vars.settings.target) {
        target = ajaxpress_vars.settings.target
    }
    
    const newContainer = newDocument.querySelector(target)

    if (!newContainer) {
        currentContainer.innerHTML = html;
        // Execute scripts from the new content
        executeScripts(currentContainer);
        return
    }

    // Handle ignore_elements (Premium feature)
    const ignoreElements = isLicenseValid ? ajaxpress_vars.settings.ignore_elements : null
    if (ignoreElements) {
        const selectors = ignoreElements.split(',').map(s => s.trim()).filter(s => s.length > 0)

        selectors.forEach(selector => {
            try {
                const currentElements = currentContainer.querySelectorAll(selector)
                const newElements = newContainer.querySelectorAll(selector)

                newElements.forEach((newEl, index) => {
                    if (currentElements[index]) {
                        newEl.replaceWith(currentElements[index].cloneNode(true))
                    }
                })
            } catch (e) {
                console.warn('AjaxPress: Invalid selector in ignore_elements:', selector)
            }
        })
    }

    // Update container HTML
    currentContainer.innerHTML = newContainer.innerHTML;
    
    // Execute scripts from the updated container content
    // Scripts are fresh after innerHTML, so they need to execute
    executeScripts(currentContainer);
    
    // Also execute scripts from the body of the new document that are outside the container
    // This ensures all scripts are executed when body content changes
    const bodyScripts = newDocument.body.querySelectorAll('script')
    if (bodyScripts.length > 0) {
        bodyScripts.forEach(oldScript => {
            // Skip scripts that are already in the container (already executed above)
            const isInContainer = newContainer.contains(oldScript)
            if (isInContainer) {
                return
            }
            
            // Skip AjaxPress's own script
            if (oldScript.src && isAjaxPressScript(oldScript.src)) {
                return
            }
            
            if (oldScript.src) {
                // External script - check if already loaded to prevent re-execution
                const normalizedSrc = normalizeUrl(oldScript.src)

                // Allow force reload with data-ajaxpress-reload attribute
                const forceReload = oldScript.hasAttribute('data-ajaxpress-reload')

                // Skip if already loaded (unless force reload)
                if (!forceReload && window.ajaxpress_loaded_scripts.has(normalizedSrc)) {
                    return
                }

                const newScript = document.createElement('script')
                Array.from(oldScript.attributes).forEach(attr => {
                    newScript.setAttribute(attr.name, attr.value)
                })
                newScript.src = normalizedSrc

                // Mark as executed
                newScript.setAttribute('data-ajaxpress-executed', 'true')

                // Track this script as loaded
                window.ajaxpress_loaded_scripts.add(normalizedSrc)

                document.body.appendChild(newScript)
            } else {
                // Inline script - execute it
                if (oldScript.textContent && oldScript.textContent.trim()) {
                    const newScript = document.createElement('script')
                    Array.from(oldScript.attributes).forEach(attr => {
                        newScript.setAttribute(attr.name, attr.value)
                    })
                    newScript.textContent = oldScript.textContent

                    // Mark as executed
                    newScript.setAttribute('data-ajaxpress-executed', 'true')

                    document.body.appendChild(newScript)
                }
            }
        })
    }
    
    // Wait for scripts to execute before triggering reinitialization events
    setTimeout(() => {
        // Trigger custom event for scripts that need to reinitialize
        emitEvent('ajaxpress-content-updated', {
            container: currentContainer,
            url: window.ajaxpress_state.url
        })
        
        // Trigger DOMContentLoaded on container for scripts that check for it
        const domContentLoadedEvent = new Event('DOMContentLoaded', { bubbles: true, cancelable: true })
        currentContainer.dispatchEvent(domContentLoadedEvent)
        
        // Trigger jQuery ready event if jQuery is available (common in WordPress)
        if (typeof jQuery !== 'undefined') {
            jQuery(currentContainer).trigger('ajaxpress:ready')
            jQuery(document).trigger('ajaxpress:ready')
            // Also trigger ready state change for scripts that check document.readyState
            if (document.readyState === 'complete') {
                jQuery(currentContainer).ready()
            }
        }
        
        // Trigger a custom ready event for scripts that might listen to it
        const readyEvent = new CustomEvent('ajaxpress:ready', { 
            bubbles: true, 
            detail: { container: currentContainer, url: window.ajaxpress_state.url }
        })
        currentContainer.dispatchEvent(readyEvent)
        document.dispatchEvent(readyEvent)
    }, 100)
}

export const loadContent = async (url, source = 'click') => {
    return new Promise ( async (resolve, reject) => {
        // Ensure we have a valid URL
        if (!url || typeof url !== 'string') {
            console.error('AjaxPress: Invalid URL provided to loadContent:', url);
            reject(new Error('Invalid URL'));
            return;
        }
        
        window.ajaxpress_state.url = url
        window.ajaxpress_state.loading = true

        try {
            const html = await fetchContent(url, source)
            await updateContent(html)

            // Fire custom event.
            emitEvent('ajaxpress-updated', {
                html: html,
                source: window.ajaxpress_state.updateSource || source
            });
            window.ajaxpress_state.loading = false
            resolve(true)
        } catch (err) {
            window.ajaxpress_state.loading = false
            console.error('AjaxPress: Error loading content:', err);
            reject(err)
        }
    })
}